/**
 * "Background" requests are the ones nobody asked for just now: a poll on a
 * timer, a refetch when the tab regains focus or the network returns, a retry,
 * the refresh after a save, a sidebar badge. When one of those fails the page
 * already shows what it has (or its own "couldn't load" line), so the request
 * layer (BaseService) must not pop a toast or send the admin to Access Denied
 * for it. A polled card that fails every 15 seconds used to put a fresh toast
 * on screen every 15 seconds.
 *
 * Marking works two ways:
 *  - a request made with `{ background: true }` in its axios config;
 *  - any request STARTED inside `runAsBackgroundRequest(fn)`. The BaseService
 *    request interceptor runs synchronously and stamps the config, so this
 *    covers the requests `fn` starts before its first `await`. Every react-query
 *    query function in this app starts its request first, which is how
 *    lib/queryClient marks refetches without touching each hook.
 */
declare module "axios" {
  // `D` must match axios's own declaration for the merge, even though it is unused here.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface AxiosRequestConfig<D = any> {
    /** Set by runAsBackgroundRequest, or by the caller: never toast or redirect. */
    background?: boolean;
    /**
     * Set by the caller that turns the failure into its own message (Impact Fund Mark as Paid): BaseService adds no toast of its
     * own for a failed answer, so the admin reads one message, not the server's wording on top of the screen's. It only silences
     * that toast: the failure still reaches the caller, and a 401 still signs out and a 403 still redirects.
     */
    handlesOwnError?: boolean;
  }
}

let depth = 0;

export function runAsBackgroundRequest<T>(fn: () => T): T {
  depth += 1;
  try {
    return fn();
  } finally {
    depth -= 1;
  }
}

export const isInBackgroundRequestScope = (): boolean => depth > 0;

export const isBackgroundRequest = (config?: { background?: boolean }): boolean =>
  Boolean(config?.background);
